/**
 * Color Maps for Different Feedback Categories
 * Extracted from ResultAndRevision.js for better organization
 */

import { getColorMapFromConfig, getValidCodesFromConfig } from './categoryGradeConfig.js';

// Base color maps for different feedback schemes
export const colorMapEnglish = {
  'P': '#2ecc71', // Purpose
  'C': '#e74c3c', // Coherence
  'L': '#3498db', // Language
  'M': '#f1c40f'  // Mechanics
};

export const colorMapAiste = {
  'G': '#16a085', // Gaeilge
  'C': '#c0392b', // Content
  'S': '#8e44ad'  // Style
};

export const colorMapGCSE = {
  'A': '#9b59b6', // Argument (purple)
  'L': '#3498db', // Language (blue) 
  'C': '#f39c12', // Context (orange)
  'M': '#e74c3c'  // Mechanics (red)
};

export const colorMapGCSELang = {
  'C': '#f39c12', // Content
  'L': '#3498db', // Language
  'A': '#e74c3c'  // Accuracy
};

// GCSE English Language - Different from Literature
export const colorMapGCSEEngLang = {
  'I': '#8e44ad', // Ideas and perspectives (purple)
  'O': '#e67e22', // Organisation (orange)
  'T': '#2ecc71', // Technical accuracy (green)
  'V': '#3498db'  // Vocabulary and sentence structure (blue)
};

// A-Level English Literature A (Love theme)
export const colorMapALevelEngLitA = {
  'A': '#e74c3c', // Argument & Expression (red)
  'F': '#9b59b6', // Form & Language (purple)
  'C': '#f39c12', // Contexts (orange)
  'N': '#27ae60', // Connections (green)
  'I': '#3498db'  // Interpretations (blue)
};

// A-Level English Literature B (Literary genres)
export const colorMapALevelEngLitB = {
  'A': '#e74c3c', // Argument & Expression (red)
  'F': '#9b59b6', // Form & Language (purple)
  'C': '#f39c12', // Contexts (orange)
  'N': '#27ae60', // Connections (green)
  'I': '#3498db'  // Interpretations (blue)
};

// A-Level French
export const colorMapALevelFrench = {
  'A': '#e74c3c', // Analysis (red)
  'E': '#3498db', // Expression (blue)
  'C': '#f39c12', // Cultural context (orange)
  'L': '#27ae60'  // Language accuracy (green)
};

// A-Level Spanish
export const colorMapALevelSpanish = {
  'A': '#e74c3c', // Analysis (red)
  'E': '#3498db', // Expression (blue)
  'C': '#f39c12', // Cultural context (orange)
  'L': '#27ae60'  // Language accuracy (green)
};

// A-Level German
export const colorMapALevelGerman = {
  'A': '#e74c3c', // Analysis (red)
  'E': '#3498db', // Expression (blue)
  'C': '#f39c12', // Cultural context (orange)
  'L': '#27ae60'  // Language accuracy (green)
};

// A-Level Psychology
export const colorMapALevelPsychology = {
  'K': '#27ae60', // Knowledge (green)
  'A': '#e74c3c', // Application (red)
  'E': '#3498db', // Evaluation (blue)
  'M': '#f39c12'  // Methodology (orange)
};

export const colorMapFrench = {
  'C': '#FF6B6B', // Communication
  'L': '#4ECDC4'  // Language
};

export const colorMapGerman = {
  'C': '#2ecc71', // Content
  'E': '#3498db'  // Expression
};

// Add Spanish color map (same as French for consistency)
export const colorMapSpanish = {
  'C': '#FF6B6B', // Communication/Content
  'L': '#4ECDC4'  // Language
};

export const colorMapAustralian = {
  'E': '#e67e22', // Engagement (orange)
  'S': '#9b59b6', // Structure (purple)
  'A': '#e74c3c', // Analysis (red)
  'L': '#3498db'  // Language (blue)
};

export const colorMapVCE_KCEL = {
  'K': '#27ae60', // Knowledge (green)
  'C': '#9b59b6', // Coherence (purple)
  'E': '#e74c3c', // Evidence (red)
  'L': '#3498db'  // Language (blue)
};

export const colorMapVCE_EPSL = {
  'E': '#e67e22', // Engagement (orange)
  'P': '#2ecc71', // Purpose (green)
  'S': '#9b59b6', // Structure (purple)
  'L': '#3498db'  // Language (blue)
};

// Combined map for backward compatibility
export const colorMapCombined = {
  ...colorMapEnglish,
  ...colorMapAiste,
  ...colorMapGCSE,
  ...colorMapVCE_KCEL,
  ...colorMapVCE_EPSL
};

export const colorMapCombinedWithGCSELang = {
  ...colorMapCombined,
  ...colorMapGCSELang
};

/**
 * Get the appropriate color map based on category
 */
export function getColorMapForCategory(category) {
  // First try to get from centralized config
  const configColorMap = getColorMapFromConfig(category);
  if (configColorMap) {
    return configColorMap;
  }
  
  // Fall back to existing logic
  if (category === 'VCE_English_SectionA' || category === 'VCE_English_SectionC') {
    return colorMapVCE_KCEL;
  } else if (category === 'VCE_English_SectionB') {
    return colorMapVCE_EPSL;
  } else if (category === 'Australian_HSC' || category === 'Australian_Textual' || category === 'Australian_Critical') {
    return colorMapAustralian;
  } else if (category === 'Shakespeare' || category === 'Poetry' || 
      category === 'Comparative' || category === 'Composition') {
    return colorMapEnglish;
  } else if (category === 'Aiste' || category === 'Grammar Checker' || 
            (category && category.startsWith('Filiocht_')) || category === 'IE_Oral') {
    return colorMapAiste;
  } else if (category && category.startsWith('FR_')) {
    return colorMapFrench;
  } else if (category && category.startsWith('DE_')) {
    return colorMapGerman;
  } else if (category && category.startsWith('ES_')) {
    return colorMapSpanish;
  } else {
    return colorMapEnglish; // Default
  }
}

/**
 * Get valid feedback codes for a category
 */
export function getValidCodesForCategory(category) {
  // First try to get from centralized config
  const configCodes = getValidCodesFromConfig(category);
  if (configCodes) {
    return configCodes;
  }
  
  // Fall back to existing logic
  if (category === 'VCE_English_SectionA' || category === 'VCE_English_SectionC') {
    return '[KCEL]'; // VCE Sections A & C
  } else if (category === 'VCE_English_SectionB') {
    return '[EPSL]'; // VCE Section B
  } else if (category === 'Australian_HSC' || category === 'Australian_Textual' || category === 'Australian_Critical') {
    return '[ESAL]'; // Australian HSC
  } else if (category === 'Aiste' || category === 'Grammar Checker' || category === 'IE_Oral') {
    return '[GCS]'; // Irish
  } else if (category && category.startsWith('Filiocht_')) {
    return '[GC]'; // Filiocht (Irish Poetry) - only Gaeilge and Content
  } else if (category && category.startsWith('FR_')) {
    return '[CL]';  // French = Communication & Language
  } else if (category && category.startsWith('DE_')) {
    return '[CE]';  // German = Content & Expression
  } else if (category && category.startsWith('ES_')) {
    return '[CL]';  // Spanish = Communication & Language
  } else {
    return '[PCLM]'; // Default English Leaving Cert
  }
}

export const getCategoryColorMap = (category) => {
  if (category === 'VCE_English_SectionA' || category === 'VCE_English_SectionC') {
    return colorMapVCE_KCEL;
  } else if (category === 'VCE_English_SectionB') {
    return colorMapVCE_EPSL;
  } else if (category === 'Australian_HSC' || category === 'Australian_Textual' || category === 'Australian_Critical' ||
      category === 'English_HSC_Textual' || category === 'English_HSC_Critical' || category === 'English_HSC_Texts' || 
      category === 'English_HSC_Craft' || category === 'English_HSC_CraftA' || category === 'English_HSC_CraftB') {
    return colorMapAustralian;
  } else if (category === 'Shakespeare' || category === 'Poetry' || 
      category === 'Comparative' || category === 'Composition') {
    return colorMapEnglish;
  } else if (category === 'Aiste' || category === 'Grammar Checker' || 
            (category && category.startsWith('Filiocht_')) || category === 'IE_Oral') {
    return colorMapAiste;
  } else if ((category && category.startsWith('FR_')) || (category && category.startsWith('ES_'))) {
    return colorMapFrench;
  } else if (category && category.startsWith('DE_')) {
    return colorMapGerman;
  } else {
    return colorMapEnglish; // Default
  }
};

export const getCategoryBorderColorMap = (category) => {
  if (category === 'Australian_HSC' || category === 'Australian_Textual' || category === 'Australian_Critical' ||
      category === 'English_HSC_Textual' || category === 'English_HSC_Critical' || category === 'English_HSC_Texts' || 
      category === 'English_HSC_Craft' || category === 'English_HSC_CraftA' || category === 'English_HSC_CraftB') {
    return colorMapAustralian;
  } else if (category === 'Shakespeare' || category === 'Poetry' || 
      category === 'Comparative' || category === 'Composition') {
    return colorMapEnglish;
  } else if (category === 'Aiste' || category === 'Grammar Checker' || 
            (category && category.startsWith('Filiocht_')) || category === 'IE_Oral') {
    return colorMapAiste;
  } else if ((category && category.startsWith('FR_')) || (category && category.startsWith('ES_'))) {
    return colorMapFrench;
  } else if (category && category.startsWith('DE_')) {
    return colorMapGerman;
  } else {
    return colorMapEnglish; // Default
  }
}; 